import { ChevronDown, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { buttonVariants } from '@src/design-system/button';
import {
  Pagination as PaginationComponent,
  PaginationItem,
  PaginationContent,
  PaginationLink,
  PaginationEllipsis,
} from '@src/design-system/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@src/design-system/select';
import { useTiclickTableContext } from './context/ticlick-table-context-hook';
import { cn } from '@src/design-system/helpers';
import { getPaginationItems } from '@shared/utils/pagination-helpers';
import TablePaginationPageInput from './table-pagination-page-input';
import { useTableStates } from './hooks/use-table-states';

export function TiclickTablePagination<T>() {
  const { table } = useTiclickTableContext<T>();
  const { pagination } = useTableStates();
  const currentPage = pagination.pageIndex + 1;
  const totalPages = table.getPageCount();

  const paginationPages = getPaginationItems({
    currentPage,
    totalPages,
  });

  return (
    <div className='flex items-start md:items-center flex-col md:flex-row justify-between gap-4 md:gap-8'>
      <div className='flex items-center gap-6'>
        <div className='flex items-center text-gray-600 text-sm'>
          <span>Showing</span>
          <p
            aria-live='polite'
            className='pl-1 font-medium text-gray-800'
          >
            <span className='text-foreground'>
              {pagination.pageIndex * pagination.pageSize + 1}-
              {Math.min(
                (pagination.pageIndex + 1) * pagination.pageSize,
                table.getRowCount()
              )}
            </span>
          </p>
          <span className='pl-1 pr-1'>of</span>
          <span className='text-foreground font-medium text-gray-800'>
            {table.getRowCount()}
          </span>
        </div>

        <div className='text-muted-foreground flex grow justify-end text-sm whitespace-nowrap'>
          <Select
            value={pagination.pageSize.toString()}
            onValueChange={(value) => table.setPageSize(Number(value))}
          >
            <SelectTrigger
              id='pagination-select'
              className='w-fit font-medium text-sm text-gray-600 rounded-lg border-primary-200 px-2 py-0.5'
              icon={<ChevronDown size={20} />}
            >
              <SelectValue
                placeholder='Select number of results'
                className='font-medium text-sm text-gray-600'
              />
            </SelectTrigger>
            <SelectContent>
              {[5, 10, 25, 50].map((pageSize) => (
                <SelectItem
                  key={pageSize}
                  value={pageSize.toString()}
                >
                  {pageSize} per page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className='flex items-center gap-6'>
        <PaginationComponent>
          <PaginationContent className='inline-flex gap-0 -space-x-px rounded-lg rtl:space-x-reverse max-h-[33px] h-[33px] border border-[#DED6EE] overflow-hidden'>
            <PaginationItem>
              <PaginationLink
                onClick={() => table.previousPage()}
                aria-label='Go to previous page'
                aria-disabled={!table.getCanPreviousPage()}
                role={table.getCanPreviousPage() ? 'button' : undefined}
                className={cn(
                  buttonVariants({ variant: 'primaryOutline' }),
                  'rounded-none shadow-none focus-visible:z-10 aria-disabled:pointer-events-none [&[aria-disabled]>svg]:opacity-50 text-[14px] border-none '
                )}
              >
                <ChevronLeftIcon
                  className='w-5! h-5! font-bold!'
                  aria-hidden='true'
                />
              </PaginationLink>
            </PaginationItem>

            {paginationPages.map((item, idx) => {
              if (item === 'LEFT_ELLIPSIS' || item === 'RIGHT_ELLIPSIS') {
                return (
                  <PaginationItem key={`${item}-${idx}`}>
                    <PaginationEllipsis
                      className={cn(
                        buttonVariants({ variant: 'primaryOutline' }),
                        'pointer-events-none rounded-none shadow-none text-[14px] border-none'
                      )}
                    />
                  </PaginationItem>
                );
              }

              return (
                <PaginationItem
                  key={item}
                  className='border border-[#DED6EE]'
                >
                  <PaginationLink
                    isActive={item === currentPage}
                    onClick={() => table.setPageIndex(item - 1)}
                    className={cn(
                      buttonVariants({ variant: 'primaryOutline' }),
                      'rounded-none shadow-none focus-visible:z-10 text-sm border-none text-gray-600',
                      item === currentPage && 'bg-accent text-gray-800'
                    )}
                  >
                    {item}
                  </PaginationLink>
                </PaginationItem>
              );
            })}

            <PaginationItem>
              <PaginationLink
                onClick={() => table.nextPage()}
                aria-label='Go to next page'
                aria-disabled={!table.getCanNextPage()}
                role={table.getCanNextPage() ? 'button' : undefined}
                className={cn(
                  buttonVariants({ variant: 'primaryOutline' }),
                  'rounded-none shadow-none focus-visible:z-10 aria-disabled:pointer-events-none [&[aria-disabled]>svg]:opacity-50 text-[14px] border-none'
                )}
              >
                <ChevronRightIcon
                  className='w-5! h-5! font-bold!'
                  aria-hidden='true'
                />
              </PaginationLink>
            </PaginationItem>
          </PaginationContent>
        </PaginationComponent>
        {/* Pagination Pages Input */}
        {totalPages > 1 && (
          <TablePaginationPageInput
            totalPages={totalPages}
            setPageIndex={table.setPageIndex}
          />
        )}
      </div>
    </div>
  );
}
